import { ProjectStatusButton } from './ProjectPlanning';
import { ProjectFileLibrary } from './ProjectFileLibrary';
import { ProjectFileSource } from './ProjectFileSource';
import { useProjectUploads } from './ProjectUploads';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  api,
  createUpload,
  post,
  useData,
  useList,
  useSession,
  orgID,
  type UploadStage,
} from '@yingbaba/api';
import {
  ActionDialog,
  Button,
  ButtonLink,
  Empty,
  ErrorNotice,
  Field,
  FileAttachment,
  Modal,
  Pagination,
  QueryState,
  Select,
  Stat,
  useToast,
  useUnsavedChanges,
  compactFileSize,
} from '@yingbaba/ui';
import { Plus, Upload, Clock, ArrowRight } from '@yingbaba/icons';
import { siteURL, date, datetime, beijingToday } from '@yingbaba/utils';
import type {
  Project,
  Confirmation,
  ProjectFile,
  Milestone,
  TaskView,
  ProjectActivity,
  ProjectInvitation,
  Provider,
} from '@yingbaba/types';

export { CreateProjectButton } from './ProjectPlanning';

export function ProjectInvitations() {
  const navigate = useNavigate();
  const q =
    useData<(ProjectInvitation & { project_title: string; organization_name: string })[]>(
      '/project-invitations',
    );
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState('');
  const qc = useQueryClient();
  async function respond(id: string, accept: boolean, projectID: string) {
    if (busy) return;
    const organization = orgID();
    setBusy(id);
    setError(null);
    try {
      await post(`/project-invitations/${id}/respond`, { accept });
      await qc.invalidateQueries();
      if (accept && organization === orgID()) navigate(`/projects/${projectID}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy('');
    }
  }
  if (!q.data?.length && !q.error) return null;
  return (
    <section className="panel">
      <h3>项目协作邀请</h3>
      <ErrorNotice error={q.error || error} />
      {q.data?.map((i) => (
        <div className="list-item" key={i.id}>
          <div className="grow">
            <strong>{i.project_title}</strong>
            <p>
              {i.organization_name} 邀请你协作 · {date(i.expires_at)}前有效
            </p>
          </div>
          <div className="row project-invitation-actions">
            <Button
              disabled={!!busy}
              loading={busy === i.id}
              onClick={() => respond(i.id, true, i.project_id)}
            >
              接受并进入项目
            </Button>
            <Button
              variant="ghost"
              disabled={!!busy}
              onClick={() => respond(i.id, false, i.project_id)}
            >
              暂不参与
            </Button>
          </div>
        </div>
      ))}
    </section>
  );
}

function InvitePartner({ project }: { project: Project }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(''),
    [search, setSearch] = useState('');
  const [error, setError] = useState<unknown>(null),
    [busy, setBusy] = useState('');
  const code = query
    .trim()
    .toUpperCase()
    .match(/^YB-ORG:([0-9A-HJKMNP-TV-Z]{26})$/);
  const providers = useData<{ organization_id: string; name: string; summary: string }[]>(
    `/project-partners?q=${encodeURIComponent(search)}`,
    open && search.length >= 2,
  );
  async function invite(organization_id: string) {
    if (busy) return;
    setBusy(organization_id);
    setError(null);
    try {
      await post(`/projects/${project.id}/invitations`, { organization_id });
      await qc.invalidateQueries();
      setOpen(false);
      toast('邀请已发送，等待对方接受');
    } catch (e) {
      setError(e);
    } finally {
      setBusy('');
    }
  }
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setQuery('');
          setSearch('');
          setError(null);
          setOpen(true);
        }}
      >
        邀请合作方
      </Button>
      <Modal
        title="邀请合作方"
        description="搜索团队或资源，也可以粘贴合作方分享的组织协作码。邀请需由对方管理员接受。"
        open={open}
        onOpenChange={(open) => {
          if (!open && !busy) setOpen(false);
        }}
      >
        <form
          className="row project-tool-search"
          onSubmit={(e) => {
            e.preventDefault();
            if (busy) return;
            setError(null);
            if (code) {
              void invite(code[1]);
            } else {
              setSearch(query.trim());
            }
          }}
        >
          <input
            className="input"
            aria-label="搜索协作团队"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="团队、资源名称或组织协作码"
            maxLength={200}
            disabled={!!busy}
          />
          <Button type="submit" loading={!!busy} disabled={query.trim().length < 2}>
            {code ? '发送邀请' : '搜索'}
          </Button>
        </form>
        <ErrorNotice error={error || providers.error} />
        {search && providers.isFetching && !code && (
          <p role="status" className="muted">
            正在查找合作方…
          </p>
        )}
        {!code &&
          providers.data?.map((p) => (
            <div className="list-item" key={p.organization_id}>
              <div className="grow">
                <strong>{p.name}</strong>
                <p>{p.summary}</p>
              </div>
              <Button
                loading={busy === p.organization_id}
                disabled={!!busy}
                onClick={() => invite(p.organization_id)}
              >
                发送邀请
              </Button>
            </div>
          ))}
        {!code && search && !providers.isFetching && providers.data && !providers.data.length && (
          <Empty title="没有找到团队" description="试试团队名称中的部分文字或擅长标签。" />
        )}
      </Modal>
    </>
  );
}

function ProjectPartnersPanel({ project, manage }: { project: Project; manage: boolean }) {
  const navigate = useNavigate();
  const q = useData<{ organization_id: string; name: string; status: string }[]>(
    `/projects/${project.id}/partners`,
  );
  return (
    <section className="panel">
      <div className="panel-title">
        <h3>协作伙伴</h3>
        {manage &&
          project.management_mode === 'tools' &&
          project.client_id === orgID() &&
          project.status === 'active' && <InvitePartner project={project} />}
      </div>
      <ErrorNotice error={q.error} />
      {q.data?.length ? (
        q.data.map((p) => (
          <div className="list-item" key={p.organization_id}>
            <div className="grow">
              <strong>{p.name}</strong>
              <p>{p.status === 'accepted' ? '已加入协作' : '等待对方管理员接受'}</p>
            </div>
            {manage && (project.client_id === orgID() || p.organization_id === orgID()) && (
              <ActionDialog
                title={
                  p.organization_id === orgID()
                    ? '退出项目协作'
                    : p.status === 'pending'
                      ? '撤回协作邀请'
                      : '移除合作方'
                }
                trigger={
                  p.organization_id === orgID()
                    ? '退出协作'
                    : p.status === 'pending'
                      ? '撤回邀请'
                      : '移除合作方'
                }
                path={`/projects/${project.id}/partners/${p.organization_id}/remove`}
                submitLabel={
                  p.organization_id === orgID()
                    ? '确认退出协作'
                    : p.status === 'pending'
                      ? '确认撤回邀请'
                      : '确认移除合作方'
                }
                intent="destructive"
                onSuccess={() => {
                  if (p.organization_id === orgID()) navigate('/projects');
                }}
                description={
                  p.status === 'pending'
                    ? '撤回后，对方将无法接受本次邀请。需要时可以重新邀请。'
                    : `${p.organization_id === orgID() ? '退出后，本组织成员' : '移除后，对方成员'}将无法继续访问项目与文件，未完成任务的负责人也会清空。已有协作记录会保留。`
                }
              />
            )}
          </div>
        ))
      ) : (
        <p className="muted">邀请客户、创作团队或资源方，一起推进项目。</p>
      )}
    </section>
  );
}

export function ToolsProjectOverview({
  project,
  milestones,
  tasks,
  activities,
  manage,
  confirmations = [],
}: {
  project: Project;
  milestones: Milestone[];
  tasks: TaskView[];
  activities: ProjectActivity[];
  manage: boolean;
  confirmations?: Confirmation[];
}) {
  const qc = useQueryClient(),
    [error, setError] = useState<unknown>(null);
  const milestoneLock = useRef(new Set<string>());
  const [milestoneBusy, setMilestoneBusy] = useState<string[]>([]);
  const done = tasks.filter((t) => t.status === 'completed').length;
  const overdue =
    project.status === 'active'
      ? tasks.filter((t) => t.status !== 'completed' && t.due_date && t.due_date < beijingToday())
          .length
      : 0;
  const canEdit = manage && project.client_id === orgID();
  const session = useSession();
  const nextTasks = tasks
    .filter((t) => project.status === 'active' && t.status !== 'completed')
    .sort((a, b) => {
      const mineA = a.assignee_account_id === session.data?.account.id,
        mineB = b.assignee_account_id === session.data?.account.id;
      return (
        Number(mineB) - Number(mineA) || (a.due_date || '9999').localeCompare(b.due_date || '9999')
      );
    })
    .slice(0, 5);
  return (
    <div className="stack">
      <ErrorNotice error={error} />
      <section className="panel">
        <div className="project-overview-header">
          <div className="project-overview-copy">
            <h2>项目工作区</h2>
            <p className="prose muted">
              {project.description || '先明确目标，再通过任务、会议与素材记录推进创作。'}
            </p>
          </div>
          <div className="row wrap project-overview-actions">
            {canEdit && (
              <ActionDialog
                title="管理项目"
                trigger="编辑项目"
                variant="secondary"
                method="PUT"
                path={`/projects/${project.id}`}
                submitLabel="保存项目信息"
                fields={[
                  { name: 'title', label: '项目名称', value: project.title, min: 2, max: 200 },
                  { name: 'category', label: '项目类型', value: project.category, required: false },
                  {
                    name: 'description',
                    label: '项目说明',
                    type: 'textarea',
                    value: project.description,
                    required: false,
                    max: 8000,
                  },
                  {
                    name: 'deadline',
                    label: '计划完成日期',
                    type: 'date',
                    value: project.deadline,
                    required: false,
                  },
                ]}
                transform={(v) => ({
                  ...v,
                  status: project.status,
                  expected_revision: project.revision,
                })}
                description="更新项目名称、目标或计划日期。详细的创作要求可在创作需求中继续完善。"
              />
            )}

            {canEdit && (
              <ProjectStatusButton
                project={project}
                tasks={tasks}
                milestones={milestones}
                confirmations={confirmations}
              />
            )}
            <ButtonLink to={`/projects/${project.id}?tab=brief`} variant="secondary">
              创作需求
              <ArrowRight size={15} />
            </ButtonLink>
            <ButtonLink to={`/promotion?project=${project.id}`} variant="secondary">
              推广计划
              <ArrowRight size={15} />
            </ButtonLink>
          </div>
        </div>
        <div className="stats project-tool-stats" style={{ marginTop: 24 }}>
          <Link
            className="project-stat-link"
            to={`/projects/${project.id}?tab=tasks`}
            aria-label="查看全部项目任务"
          >
            <Stat label="任务完成" value={`${done} / ${tasks.length}`} />
          </Link>
          <Link
            className={`project-stat-link${overdue ? ' has-overdue' : ''}`}
            to={`/projects/${project.id}?tab=tasks&task_status=overdue`}
            aria-label="查看逾期项目任务"
          >
            <Stat label="逾期待办" value={overdue} />
          </Link>
          <Stat label="计划完成" value={date(project.deadline)} />
        </div>
      </section>
      {!tasks.length && project.status === 'active' && (
        <section className="panel project-first-task">
          <div>
            <h3>安排第一项任务</h3>
            <p className="muted">把项目目标拆成具体待办，明确负责人和截止日期。</p>
          </div>
          <ButtonLink to={`/projects/${project.id}?tab=tasks`} variant="secondary">
            前往任务 <ArrowRight size={15} />
          </ButtonLink>
        </section>
      )}
      {nextTasks.length > 0 && (
        <section className="panel">
          <div className="panel-title">
            <h3>接下来要做</h3>
            <Link to={`/projects/${project.id}?tab=tasks`}>全部任务</Link>
          </div>
          {nextTasks.map((t) => (
            <Link
              className="list-item project-next-task"
              key={t.id}
              to={`/projects/${project.id}?tab=tasks&task=${t.id}`}
            >
              <div className="grow">
                <strong>{t.title}</strong>
                <p className="small muted">
                  {t.assignee_account_id === session.data?.account.id
                    ? '分配给我'
                    : t.assignee || '未指定负责人'}{' '}
                  · {t.due_date ? `截止 ${date(t.due_date)}` : '未设截止日期'}
                </p>
              </div>
              <ArrowRight size={16} />
            </Link>
          ))}
        </section>
      )}
      <ProjectPartnersPanel project={project} manage={manage} />
      <div className="grid-2 project-overview-secondary">
        <section className="panel">
          <div className="panel-title">
            <h3>制作里程碑</h3>
            {manage && !['completed', 'cancelled'].includes(project.status) && (
              <ActionDialog
                title="新增里程碑"
                trigger="新增阶段"
                path={`/projects/${project.id}/milestones`}
                submitLabel="添加里程碑"
                fields={[
                  { name: 'title', label: '阶段名称', min: 2, max: 200 },
                  { name: 'due_date', label: '计划日期', type: 'date', required: false },
                ]}
              />
            )}
          </div>
          {milestones.length ? (
            milestones.map((m) => (
              <div key={m.id} className="list-item">
                <div className="grow">
                  <strong>{m.title}</strong>
                  <p>
                    {m.due_date ? date(m.due_date) : '未安排日期'} ·{' '}
                    {m.status === 'completed' ? '已完成' : '待完成'}
                  </p>
                </div>
                <div className="row wrap project-stage-actions">
                  {manage && project.status === 'active' && (
                    <ActionDialog
                      title="编辑制作阶段"
                      trigger="编辑阶段"
                      variant="ghost"
                      method="PUT"
                      path={`/projects/${project.id}/milestones/${m.id}`}
                      submitLabel="保存阶段"
                      fields={[
                        { name: 'title', label: '阶段名称', value: m.title, min: 2, max: 200 },
                        {
                          name: 'due_date',
                          label: '计划日期',
                          type: 'date',
                          required: false,
                          value: m.due_date,
                        },
                        {
                          name: 'status',
                          label: '阶段状态',
                          value: m.status,
                          options: [
                            { value: 'pending', label: '待完成' },
                            { value: 'completed', label: '已完成' },
                          ],
                        },
                      ]}
                      transform={(v) => ({ ...v, expected_updated_at: m.updated_at })}
                      description="可调整名称、日期，或将已完成阶段重新设为待完成。阶段状态不会自动改变任务状态。"
                    />
                  )}
                  {manage && m.status !== 'completed' && project.status === 'active' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={milestoneBusy.includes(m.id)}
                      onClick={async () => {
                        if (milestoneLock.current.has(m.id)) return;
                        milestoneLock.current.add(m.id);
                        setMilestoneBusy([...milestoneLock.current]);
                        setError(null);
                        try {
                          await post(`/collaboration/milestones/${m.id}/complete`, {});
                          await qc.invalidateQueries();
                        } catch (e) {
                          setError(e);
                        } finally {
                          milestoneLock.current.delete(m.id);
                          setMilestoneBusy([...milestoneLock.current]);
                        }
                      }}
                    >
                      标记完成
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <Empty
              title="把工作拆成几个阶段"
              description="例如策划、拍摄、后期与发布，给团队一个清晰节奏。"
            />
          )}
        </section>
        <section className="panel">
          <h3>最近动态</h3>
          {activities.length ? (
            activities.slice(0, 6).map((a) => (
              <div key={a.id} className="list-item">
                <div>
                  <strong>{a.title}</strong>
                  <p>{a.content}</p>
                  <small className="muted">
                    {a.actor_name} · {datetime(a.created_at)}
                  </small>
                </div>
              </div>
            ))
          ) : (
            <Empty title="协作动态将在这里积累" />
          )}
        </section>
      </div>
    </div>
  );
}

export function ProjectFilesPanel(props: { projectID: string; write: boolean }) {
  const session = useSession();
  const role = session.data?.organizations.find((o) => o.id === orgID())?.role || '';
  return (
    <ScopedProjectFilesPanel
      key={`${props.projectID}:${orgID()}:${session.data?.account.id}:${role}:${props.write}`}
      {...props}
    />
  );
}
function ScopedProjectFilesPanel({ projectID, write }: { projectID: string; write: boolean }) {
  const [page, setPage] = useState(1),
    [error, setError] = useState<unknown>(null),
    [busy, setBusy] = useState(false);
  const [fileQuery, setFileQuery] = useState(''),
    [fileSearch, setFileSearch] = useState(''),
    [fileType, setFileType] = useState('');
  const files = useList<ProjectFile>(
    `/projects/${projectID}/files?page=${page}&q=${encodeURIComponent(fileSearch)}&type=${fileType}`,
  );
  const [versionTarget, setVersionTarget] = useState<ProjectFile | null>(null),
    [note, setNote] = useState('');
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionError, setVersionError] = useState<unknown>(null);
  const [versionStage, setVersionStage] = useState<UploadStage | null>(null);
  const versionAttempt = useRef<ReturnType<typeof createUpload> | null>(null);
  const versionPending = useRef(false);
  const versionErrorTarget = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (versionError) versionErrorTarget.current?.scrollIntoView({ block: 'nearest' });
  }, [versionError]);
  const [history, setHistory] = useState<ProjectFile[] | null>(null),
    [historyLoading, setHistoryLoading] = useState('');
  const historyLock = useRef(false);
  const historyOpener = useRef<HTMLButtonElement | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const uploads = useProjectUploads(projectID, write, () => {
    setPage(1);
    void qc.invalidateQueries({
      predicate: (q) => /files|file-library/.test(String(q.queryKey[0])),
    });
  });
  useEffect(() => () => versionAttempt.current?.cancel(), []);
  useUnsavedChanges({
    dirty: !!versionTarget && busy,
    title: '文件新版本尚未保存',
    description: '离开后会停止本次上传，旧版本不受影响。已提交保存的结果可在版本记录中核对。',
    continueLabel: '继续等待',
    discardLabel: '停止上传并离开',
    discard: () => {
      versionAttempt.current?.cancel();
    },
  });
  function chooseVersion(file?: File) {
    if (input.current) input.current.value = '';
    if (!file || versionPending.current) return;
    if (!file.size || file.size > 100 * 1024 * 1024) {
      setVersionError(new Error(file.size ? '单个附件最大 100 MB' : '文件为空，请重新选择'));
      return;
    }
    versionAttempt.current?.cancel();
    versionAttempt.current = null;
    setVersionError(null);
    setVersionFile(file);
  }
  async function upload() {
    if (!versionFile || !versionTarget || versionPending.current) return;
    versionPending.current = true;
    setBusy(true);
    setVersionError(null);
    try {
      if (!versionAttempt.current) {
        const data = new FormData();
        data.append('file', versionFile);
        data.append('version_of', versionTarget.id);
        data.append('expected_version', String(versionTarget.version || 1));
        data.append('version_note', note);
        versionAttempt.current = createUpload(`/projects/${projectID}/files`, data);
      }
      await versionAttempt.current.run(setVersionStage);
      await qc.invalidateQueries();
      setVersionTarget(null);
      setNote('');
      setVersionFile(null);
      versionAttempt.current = null;
      setPage(1);
    } catch (e) {
      setVersionError(e);
    } finally {
      versionPending.current = false;
      setBusy(false);
      setVersionStage(null);
    }
  }
  return (
    <section className="panel">
      <div className="panel-title">
        <div>
          <h3>项目素材</h3>
          <p className="muted small">
            支持图片、视频、文档等文件，每次最多 20 个，单个最大 100 MB。
          </p>
        </div>
        {write && (
          <div className="row wrap">
            <ProjectFileLibrary projectID={projectID} onAdded={() => setPage(1)} />
            <ButtonLink to={siteURL('www', '/digital-resources')} variant="secondary">
              寻找数字素材
              <ArrowRight size={15} />
            </ButtonLink>
            <Button
              loading={busy}
              onClick={() => {
                uploads.choose();
              }}
            >
              <Upload size={16} />
              上传文件
            </Button>
          </div>
        )}
      </div>
      {uploads.input}
      {uploads.queue}
      <ErrorNotice error={error} />
      <form
        className="project-file-filters"
        role="search"
        aria-label="查找项目文件"
        onSubmit={(e) => {
          e.preventDefault();
          setFileSearch(fileQuery.trim());
          setPage(1);
        }}
      >
        <div className="project-file-search">
          <input
            className="input"
            aria-label="项目文件关键词"
            placeholder="搜索文件名"
            value={fileQuery}
            maxLength={200}
            onChange={(e) => setFileQuery(e.target.value)}
          />
          <Button variant="secondary" type="submit">
            搜索
          </Button>
        </div>
        <Select
          aria-label="文件类型"
          value={fileType}
          onChange={(v) => {
            setFileType(v);
            setPage(1);
          }}
          options={[
            { value: '', label: '全部类型' },
            { value: 'image', label: '图片' },
            { value: 'video', label: '视频' },
            { value: 'audio', label: '音频' },
            { value: 'document', label: '文档' },
            { value: 'other', label: '其他文件' },
          ]}
        />
      </form>
      <QueryState query={files}>
        {!files.data?.items.length && (
          <Empty
            title={fileSearch || fileType ? '没有找到匹配的文件' : '整理项目的创作素材'}
            description={
              fileSearch || fileType
                ? '试试其他关键词或文件类型。'
                : '上传脚本、分镜、图片和成片，或从素材库添加已有文件。'
            }
          />
        )}
        <div className="stack">
          {files.data?.items.map((f) => (
            <article className="project-file-version" key={f.id}>
              <FileAttachment
                name={f.name}
                mime={f.mime}
                size={f.size}
                createdAt={f.created_at}
                previewPath={`/files/${f.id}/preview`}
                downloadPath={`/files/${f.id}/download`}
              />
              <div className="row spread wrap">
                <span className="small muted">
                  V{f.version || 1}
                  {f.source_file_id ? ' · 来自素材库' : ''}
                  {f.version_note ? ` · ${f.version_note}` : ''}
                </span>
                <div className="row wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={historyLoading === f.id}
                    disabled={!!historyLoading && historyLoading !== f.id}
                    onClick={async (event) => {
                      if (historyLock.current) return;
                      historyOpener.current = event.currentTarget;
                      historyLock.current = true;
                      setError(null);
                      setHistoryLoading(f.id);
                      try {
                        setHistory(await api<ProjectFile[]>(`/files/${f.id}/versions`));
                      } catch (e) {
                        setError(e);
                      } finally {
                        historyLock.current = false;
                        setHistoryLoading('');
                      }
                    }}
                  >
                    <Clock size={14} />
                    版本记录
                  </Button>
                  <ProjectFileSource
                    file={f}
                    write={write && f.organization_id === orgID()}
                    onChanged={() => setPage(1)}
                  />
                  {write && f.organization_id === orgID() && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        versionAttempt.current?.cancel();
                        versionAttempt.current = null;
                        setVersionTarget(f);
                        setNote('');
                        setVersionFile(null);
                        setVersionError(null);
                      }}
                    >
                      上传新版本
                    </Button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </QueryState>
      <Pagination
        page={page}
        size={files.data?.page_size || 30}
        total={files.data?.total || 0}
        onPage={setPage}
      />
      <Modal
        title="上传文件新版本"
        description="旧版本会保留，其他项目和推广计划中已选用的素材保持不变。"
        open={!!versionTarget}
        busy={busy}
        onOpenChange={(open) => {
          if (!open && !busy) {
            versionAttempt.current?.cancel();
            versionAttempt.current = null;
            setVersionTarget(null);
            setVersionFile(null);
          }
        }}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                versionAttempt.current?.cancel();
                versionAttempt.current = null;
                setVersionTarget(null);
                setVersionFile(null);
              }}
            >
              取消
            </Button>
            <Button
              type="submit"
              form={`version-upload-${projectID}`}
              disabled={!versionFile}
              loading={busy}
            >
              {versionError ? '重试上传' : '上传新版本'}
            </Button>
          </>
        }
      >
        <form
          id={`version-upload-${projectID}`}
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void upload();
          }}
        >
          <div className="version-upload-current">
            <span className="small muted">正在更新的文件 · V{versionTarget?.version || 1}</span>
            <strong title={versionTarget?.name}>{versionTarget?.name}</strong>
          </div>
          <Field
            label="新版本文件"
            htmlFor="project-version-file-button"
            hint="支持图片、视频、音频、文档及制作文件，单个最大 100 MB。选择后点击上传，旧版本不会被覆盖。"
          >
            <input
              type="file"
              ref={input}
              className="sr-only"
              tabIndex={-1}
              disabled={busy}
              aria-label="选择文件新版本"
              onChange={(e) => chooseVersion(e.target.files?.[0])}
            />
            <div className="version-upload-file">
              <div>
                <strong title={versionFile?.name}>
                  {versionFile?.name || '选择要上传的新文件'}
                </strong>
                {versionFile && (
                  <span className="small muted">{compactFileSize(versionFile.size)}</span>
                )}
              </div>
              <Button
                id="project-version-file-button"
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => input.current?.click()}
              >
                <Upload size={16} />
                {versionFile ? '重新选择' : '选择文件'}
              </Button>
            </div>
          </Field>
          <Field
            label="版本说明（可选）"
            htmlFor="project-file-version-note"
            hint="简要说明这次修改，便于团队查阅版本记录。"
          >
            <textarea
              id="project-file-version-note"
              value={note}
              maxLength={500}
              rows={3}
              disabled={busy}
              placeholder="例如：调整片尾字幕，替换确认后的配音"
              onChange={(e) => {
                versionAttempt.current?.cancel();
                versionAttempt.current = null;
                setNote(e.target.value);
              }}
            />
          </Field>
          {versionStage && (
            <p className="small muted" role="status">
              {
                {
                  preparing: '正在准备上传…',
                  uploading: '正在上传文件，请稍候…',
                  confirming: '文件已上传，正在保存版本…',
                }[versionStage]
              }
            </p>
          )}
          {!!versionError && (
            <div ref={versionErrorTarget}>
              <ErrorNotice error={versionError} />
            </div>
          )}
        </form>
      </Modal>
      <Modal
        title="文件版本记录"
        description="查看、预览或下载每次保存的文件版本。"
        open={!!history}
        returnFocusRef={historyOpener}
        onOpenChange={(open) => {
          if (!open) setHistory(null);
        }}
      >
        <div className="stack">
          {history?.map((f, index) => (
            <div className="project-file-version" key={f.id}>
              <strong>
                V{f.version || 1} · {index === 0 ? '当前版本' : '历史版本'}
                {f.version_note ? ` · ${f.version_note}` : ''}
              </strong>
              <FileAttachment
                name={f.name}
                mime={f.mime}
                size={f.size}
                createdAt={f.created_at}
                previewPath={`/files/${f.id}/preview`}
                downloadPath={`/files/${f.id}/download`}
              />
              <ProjectFileSource file={f} write={false} onChanged={() => setPage(1)} />
            </div>
          ))}
        </div>
      </Modal>
    </section>
  );
}
